import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Edit, Trash2, Settings, GripVertical, Clock, Calendar as CalendarIcon,
  User, Users, Tag, Paperclip, MessageSquare, Activity,
  Play, Square, Timer, X, CheckCircle2, AlertTriangle, ArrowUpRight,
  Loader2, PieChart, Eye, Link,
  ChevronDown, ChevronUp, Save,
  ClipboardList, Target, Zap, FileText, Upload, Download, ExternalLink, Copy, Code, Video,
  GitBranch, Info
} from 'lucide-react';
import {
  DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors,
  DragStartEvent, DragEndEvent, useDroppable
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, isToday, isPast } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { useIsMobile } from '@/hooks/use-media-query';
import { apiClient, getPublicBaseUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import BoardSettingsModal, { type BoardCreateInput, type BoardDraft } from '@/components/tasks/BoardSettingsModal';
import TaskSettingsModal from '@/pages/impostazioni/TaskSettings';
import { BoardsAuroraSidebar, BoardsAuroraDrawer } from '@/components/tasks/BoardsAuroraSidebar';
import { TasksAuroraHeader, MobileFiltersSheet, MobileNewTaskFab, type ViewKey } from '@/components/tasks/TasksAuroraHeader';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';
import { getErrorMessage } from '@/lib/utils';
import type { BoardWorkflowStat, BoardWorkflowRun, BoardAvailableWorkflow, BoardWorkflowNode } from '@easyflow/shared';


interface TriggerNode {
  id: string;
  output_schema?: { properties?: Record<string, { context?: string;[key: string]: unknown }> };
  [key: string]: unknown;
}

interface Board {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  is_default: boolean;
  position: number;
  task_count?: number;
  board_type?: string;
  workgroup_id?: string;
  workgroup_name?: string;
  is_template?: boolean;
  created_by?: string;
}

interface BoardStatus {
  id: string;
  board_id: string;
  name: string;
  color: string;
  icon?: string;
  position: number;
  is_default: boolean;
  is_completed: boolean;
  is_cancelled: boolean;
}

interface TaskTag {
  id: string;
  name: string;
  color: string;
}

interface TaskItem {
  id: string;
  code: string;
  title: string;
  description?: string;
  priority: string;
  status_id: string;
  status_name?: string;
  status_color?: string;
  board_id: string;
  assignee_id?: string;
  assignee_first_name?: string;
  assignee_last_name?: string;
  reporter_id?: string;
  due_date?: string;
  start_date?: string;
  estimated_hours?: number;
  appointment_date?: string;
  appointment_duration?: number;
  crm_lead_id?: string;
  crm_customer_id?: string;
  call_uuid?: string;
  source: string;
  position: number;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  tags?: TaskTag[];
  subtasks?: Subtask[];
  watchers?: Array<{ id?: string; user_id?: string; first_name?: string; last_name?: string }>;
  dependencies?: Array<{ id?: string; depends_on_task_id?: string; task_id?: string; code?: string; title?: string }>;
  attachments_count?: number;
  comments_count?: number;
  time_total_seconds?: number;
  time_stats?: { total_seconds: number; total_hours: number; entry_count: number };
  subtask_total?: number;
  subtask_completed?: number;
}

interface Subtask {
  id: string;
  title: string;
  is_completed: boolean;
  position: number;
  completed_at?: string;
}

type TaskDetail = TaskItem & { blocked_by?: TaskItem['dependencies']; blocking?: TaskItem['dependencies'] };

interface TimeEntry {
  id: string;
  user_id: string;
  started_at: string;
  ended_at?: string;
  duration_seconds?: number;
  description?: string;
  is_running: boolean;
  user_first_name?: string;
  user_last_name?: string;
}

interface TaskComment {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_first_name?: string;
  user_last_name?: string;
}

interface TaskActivity {
  id: string;
  user_id?: string;
  action: string;
  old_value?: unknown;
  new_value?: unknown;
  metadata?: Record<string, unknown>;
  created_at: string;
  user_first_name?: string;
  user_last_name?: string;
}

interface BoardStats {
  total: number;
  by_status: { id: string; name: string; color: string; count: number }[];
  by_priority: { priority: string; count: number }[];
  by_assignee: { id: string; name: string; count: number }[];
  overdue_count: number;
  avg_completion_hours: number;
  completed_this_week: number;
  created_this_week: number;
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; emoji: string }> = {
  urgent_important: { label: 'Urgente + Importante', color: 'text-red-700 dark:text-red-300', bg: 'bg-red-500/10 dark:bg-red-500/20', border: 'border-red-500/30', emoji: '🔴' },
  not_urgent_important: { label: 'Importante', color: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-500/10 dark:bg-yellow-500/20', border: 'border-yellow-500/30', emoji: '🟡' },
  urgent_not_important: { label: 'Urgente', color: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-500/10 dark:bg-orange-500/20', border: 'border-orange-500/30', emoji: '🟠' },
  not_urgent_not_important: { label: 'Né urgente né importante', color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', emoji: '⚪' },
  none: { label: 'Nessuna', color: 'text-muted-foreground', bg: 'bg-muted/50', border: 'border-border', emoji: '—' },
};

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

function SortableTaskCard({ task, onClick }: { task: TaskItem; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const { t } = useAuroraTheme();
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background: t.panel,
    borderColor: isDragging ? t.primary : t.border,
    boxShadow: isDragging
      ? `0 12px 32px -10px ${t.primary}66`
      : `0 1px 2px ${t.border}`,
  };
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.none;
  const isOverdue = task.due_date && !task.completed_at && isPast(new Date(task.due_date));
  const subtaskPct = task.subtask_total ? Math.round((task.subtask_completed || 0) / task.subtask_total * 100) : 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border transition-all cursor-pointer group hover:-translate-y-px ${isDragging ? 'z-50' : ''}`}
      onMouseEnter={(e) => { if (!isDragging) e.currentTarget.style.boxShadow = `0 6px 18px -8px ${t.borderStrong}`; }}
      onMouseLeave={(e) => { if (!isDragging) e.currentTarget.style.boxShadow = `0 1px 2px ${t.border}`; }}
    >
      <div className="h-1 rounded-t-xl" style={{ backgroundColor: task.status_color || t.primary }} />
      <div className="p-3" onClick={onClick}>
        <div className="flex items-start gap-2 mb-1">
          <div {...attributes} {...listeners} className="text-muted-foreground/50 hover:text-muted-foreground cursor-grab mt-0.5" onClick={e => e.stopPropagation()}>
            <GripVertical className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] font-mono text-muted-foreground">{task.code}</span>
              <Badge className={`${priority!.bg!} ${priority!.color!} ${priority!.border!} text-[9px] px-1.5 py-0 border`}>{priority!.emoji!} {priority!.label!}</Badge>
            </div>
            <h4 className="text-sm font-semibold text-foreground leading-snug">{task.title}</h4>
          </div>
        </div>

        {task.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-2 ml-5.5 leading-relaxed">{task.description}</p>
        )}

        <div className="flex items-center justify-between mb-1.5">
          {task.assignee_first_name ? (
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-medium ring-2 ring-card shadow-sm">
                {task.assignee_first_name[0]}{task.assignee_last_name?.[0] || ''}
              </div>
              <span className="text-xs text-muted-foreground font-medium truncate">{task.assignee_first_name} {task.assignee_last_name || ''}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground/50">
              <User className="w-3.5 h-3.5" />
              <span className="text-[10px]">Non assegnato</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            {(task.attachments_count || 0) > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <Paperclip className="w-3 h-3" />{task.attachments_count}
              </span>
            )}
            {(task.comments_count || 0) > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <MessageSquare className="w-3 h-3" />{task.comments_count}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {isOverdue && (
            <Badge className="bg-red-500/10 dark:bg-red-500/20 text-red-700 dark:text-red-300 text-[9px] px-1.5 py-0.5 border border-red-500/30">
              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Scaduto
            </Badge>
          )}
          {task.due_date && !isOverdue && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border text-muted-foreground font-normal">
              <CalendarIcon className="w-2.5 h-2.5 mr-0.5" />{format(new Date(task.due_date), 'dd/MM/yy')}
            </Badge>
          )}
          {task.appointment_date && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-500/30 text-purple-600 dark:text-purple-300 font-normal">
              <Clock className="w-2.5 h-2.5 mr-0.5" />{format(new Date(task.appointment_date), 'dd/MM HH:mm')}
            </Badge>
          )}
          {task.estimated_hours && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border text-muted-foreground font-normal">
              <Timer className="w-2.5 h-2.5 mr-0.5" />{task.estimated_hours}h
            </Badge>
          )}
        </div>

        {task.tags && task.tags.length > 0 && (
          <div className="flex gap-1 mt-2 flex-wrap">
            {task.tags.slice(0, 3).map(tag => (
              <span key={tag.id} className="text-[9px] px-1.5 py-0.5 rounded-full border font-medium" style={{ backgroundColor: tag.color + '25', color: tag.color, borderColor: tag.color + '50' }}>{tag.name}</span>
            ))}
            {task.tags.length > 3 && <span className="text-[9px] text-muted-foreground">+{task.tags.length - 3}</span>}
          </div>
        )}

        {task.subtask_total && task.subtask_total > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[10px] text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> {task.subtask_completed}/{task.subtask_total} subtask</span>
              <span className="text-[10px] font-medium text-muted-foreground">{subtaskPct}%</span>
            </div>
            <Progress value={subtaskPct} className="h-1.5" />
          </div>
        )}
      </div>
    </div>
  );
}

function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const { t } = useAuroraTheme();
  return (
    <div
      ref={setNodeRef}
      className="min-h-[100px] transition-colors rounded-lg"
      style={isOver ? {
        background: `${t.primary}10`,
        boxShadow: `inset 0 0 0 2px ${t.primary}55`,
      } : undefined}
    >
      {children}
    </div>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t: aurT } = useAuroraTheme();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sidebarPinnedKey = useMemo(() => `tasks.aurora.sidebar.pinned.${user?.id || 'anon'}`, [user?.id]);
  const [sidebarPinned, setSidebarPinned] = useState<boolean>(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(`tasks.aurora.sidebar.pinned.${user?.id || 'anon'}`) : null;
      return v === '1';
    } catch { return false; }
  });
  // Rehydrate when user identity becomes known/changes so per-user preference is honored
  // without overwriting the stored value of a different user.
  const sidebarPinnedHydratedKeyRef = useRef<string>(sidebarPinnedKey);
  useEffect(() => {
    if (sidebarPinnedHydratedKeyRef.current === sidebarPinnedKey) return;
    sidebarPinnedHydratedKeyRef.current = sidebarPinnedKey;
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem(sidebarPinnedKey) : null;
      setSidebarPinned(v === '1');
    } catch { /* ignore */ }
  }, [sidebarPinnedKey]);
  useEffect(() => {
    // Only persist after hydration for the current key, to avoid overwriting another user's value.
    if (sidebarPinnedHydratedKeyRef.current !== sidebarPinnedKey) return;
    try { localStorage.setItem(sidebarPinnedKey, sidebarPinned ? '1' : '0'); } catch {}
  }, [sidebarPinned, sidebarPinnedKey]);

  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [statuses, setStatuses] = useState<BoardStatus[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [tags, setTags] = useState<TaskTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<ViewKey>('kanban');
  const [users, setUsers] = useState<Array<{ id: string; first_name?: string; last_name?: string; email?: string }>>([]);

  const activeViewKey = useMemo(() => (
    selectedBoardId ? `tasks.aurora.activeView.${user?.id || 'anon'}.${selectedBoardId}` : ''
  ), [user?.id, selectedBoardId]);
  useEffect(() => {
    if (!activeViewKey) return;
    try {
      const v = localStorage.getItem(activeViewKey) as ViewKey | null;
      if (v && ['kanban','table','eisenhower','kpi'].includes(v)) {
        setActiveView(v);
      }
    } catch {}
  }, [activeViewKey]);
  const handleChangeView = useCallback((v: ViewKey) => {
    setActiveView(v);
    try { if (activeViewKey) localStorage.setItem(activeViewKey, v); } catch {}
  }, [activeViewKey]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterTag, setFilterTag] = useState('');

  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [showTaskSettings, setShowTaskSettings] = useState(false);
  const [boardModalMode, setBoardModalMode] = useState<'create' | 'edit'>('edit');
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [taskDetail, setTaskDetail] = useState<TaskDetail | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  const [newTask, setNewTask] = useState({ title: '', board_id: '', status_id: '', priority: 'none', assignee_id: '', due_date: '', tags: [] as string[], watcher_ids: [] as string[] });
  const [editBoard, setEditBoard] = useState<Board | null>(null);
  const [boardStatuses, setBoardStatuses] = useState<BoardStatus[]>([]);

  const [draggedTask, setDraggedTask] = useState<TaskItem | null>(null);

  const [comments, setComments] = useState<TaskComment[]>([]);
  const [activities, setActivities] = useState<TaskActivity[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [newComment, setNewComment] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  const [activeTimer, setActiveTimer] = useState<TimeEntry | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);


  const [boardWorkflowStats, setBoardWorkflowStats] = useState<BoardWorkflowStat[]>([]);
  const [boardWorkflowRuns, setBoardWorkflowRuns] = useState<BoardWorkflowRun[]>([]);
  const [boardAvailableWorkflows, setBoardAvailableWorkflows] = useState<BoardAvailableWorkflow[]>([]);
  const [boardCompatibleTriggerTypes, setBoardCompatibleTriggerTypes] = useState<string[]>([]);
  const [showAddBoardWorkflow, setShowAddBoardWorkflow] = useState(false);
  const [boardWorkflowForm, setBoardWorkflowForm] = useState({ workflow_id: '' });
  const [savingBoardWorkflow, setSavingBoardWorkflow] = useState(false);



  const [boardStats, setBoardStats] = useState<BoardStats | null>(null);
  const [eisenhowerMatrix, setEisenhowerMatrix] = useState<Record<string, unknown> | null>(null);

  const [detailTab, setDetailTab] = useState('info');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingTask, setSavingTask] = useState(false);

  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadBoards = useCallback(async () => {
    try {
      const res = await apiClient.get<Board[]>('/tasks/boards');
      if (res.success && Array.isArray(res.data)) {
        setBoards(res.data);
        if (!selectedBoardId && res.data.length > 0) {
          setSelectedBoardId(res.data[0]!.id!);
        }
      }
    } catch (e) { console.error('loadBoards error', e); }
  }, [selectedBoardId]);

  const loadStatuses = useCallback(async () => {
    if (!selectedBoardId) return;
    try {
      const res = await apiClient.get<{ statuses?: BoardStatus[] }>(`/tasks/boards/${selectedBoardId}/statuses`);
      if (res.success && res.data) {
        setStatuses(res.data.statuses || []);
      }
    } catch (e) { console.error('loadStatuses error', e); }
  }, [selectedBoardId]);

  const loadTasks = useCallback(async () => {
    if (!selectedBoardId) return;
    try {
      const params = new URLSearchParams();
      params.set('board_id', selectedBoardId);
      params.set('limit', '50');
      if (searchQuery) params.set('search', searchQuery);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterAssignee) params.set('assignee_id', filterAssignee);
      if (filterTag) params.set('tags', filterTag);

      const res = await apiClient.get<TaskItem[]>(`/tasks?${params.toString()}`);
      if (res.success && Array.isArray(res.data)) {
        setTasks(res.data);
      }
    } catch (e) { console.error('loadTasks error', e); }
  }, [selectedBoardId, searchQuery, filterPriority, filterAssignee, filterTag]);

  const loadTags = useCallback(async () => {
    try {
      const res = await apiClient.get<TaskTag[]>('/tasks/tags');
      if (res.success && Array.isArray(res.data)) setTags(res.data);
    } catch (e) { console.error('loadTags error', e); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await apiClient.get<{ users?: Array<{ id: string; first_name?: string; last_name?: string; email?: string }> } | Array<{ id: string; first_name?: string; last_name?: string; email?: string }>>('/users');
      if (res.success && res.data) {
        const list = Array.isArray(res.data) ? res.data : (res.data.users || []);
        setUsers(list);
      }
    } catch (e) { console.error('loadUsers error', e); }
  }, []);

  const loadBoardStats = useCallback(async () => {
    if (!selectedBoardId) return;
    try {
      const res = await apiClient.get<BoardStats>(`/tasks/stats?board_id=${selectedBoardId}`);
      if (res.success && res.data) setBoardStats(res.data);
    } catch (e) { console.error('loadBoardStats error', e); }
  }, [selectedBoardId]);

  const loadEisenhowerMatrix = useCallback(async () => {
    try {
      const params = selectedBoardId ? `?board_id=${selectedBoardId}` : '';
      const res = await apiClient.get<Record<string, unknown>>(`/tasks/eisenhower${params}`);
      if (res.success && res.data) setEisenhowerMatrix(res.data);
    } catch (e) { console.error('loadEisenhower error', e); }
  }, [selectedBoardId]);


  const loadActiveTimer = useCallback(async () => {
    try {
      const res = await apiClient.get<TimeEntry>('/tasks/my-timer');
      if (res.success && res.data) {
        setActiveTimer(res.data);
        if (res.data.is_running) {
          const elapsed = Math.floor((Date.now() - new Date(res.data.started_at).getTime()) / 1000);
          setTimerSeconds(elapsed);
        }
      } else {
        setActiveTimer(null);
      }
    } catch (e) { console.error('loadActiveTimer error', e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadBoards(),
        loadTags(),
        loadUsers(),
        loadActiveTimer(),
      ]);
      setLoading(false);
    };
    init();
  }, [loadActiveTimer, loadBoards, loadTags, loadUsers]);

  useEffect(() => {
    if (selectedBoardId) {
      loadStatuses();
      loadTasks();
    }
  }, [selectedBoardId, loadStatuses, loadTasks]);

  useEffect(() => {
    if (activeView === 'kpi' && selectedBoardId) loadBoardStats();
    if (activeView === 'eisenhower') loadEisenhowerMatrix();
  }, [activeView, selectedBoardId, loadBoardStats, loadEisenhowerMatrix]);

  useEffect(() => {
    if (activeTimer?.is_running) {
      timerInterval.current = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => { if (timerInterval.current) clearInterval(timerInterval.current); };
  }, [activeTimer?.is_running]);

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleOpenCreateBoard = () => {
    setEditBoard(null);
    setBoardStatuses([]);
    setBoardModalMode('create');
    setShowBoardSettings(true);
  };

  const handleCreateBoard = async (data: BoardCreateInput) => {
    try {
      const res = await apiClient.post('/tasks/boards', {
        name: data.name,
        description: data.description,
        color: data.color,
        board_type: data.board_type,
        is_template: data.is_template,
        workgroup_id: data.workgroup_id,
      });
      if (res.success) {
        toast({ title: 'Board creata' });
        setShowBoardSettings(false);
        await loadBoards();
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleDeleteBoard = async (boardId: string) => {
    if (!confirm('Eliminare questa board e tutti i task?')) return;
    try {
      const res = await apiClient.delete(`/tasks/boards/${boardId}`);
      if (res.success) {
        toast({ title: 'Board eliminata' });
        if (selectedBoardId === boardId) setSelectedBoardId('');
        await loadBoards();
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleCreateTask = async () => {
    try {
      const boardId = newTask.board_id || selectedBoardId;
      if (!boardId) {
        toast({ title: 'Errore', description: 'Seleziona o crea una board prima di aggiungere un task', variant: 'destructive' });
        return;
      }
      let statusId: string | undefined = newTask.status_id || undefined;
      if (!statusId) {
        statusId = statuses.find(s => s.is_default)?.id ?? statuses[0]?.id;
      }
      type CreateTaskPayload = {
        title: string;
        board_id: string;
        priority: string;
        status_id?: string;
        assignee_id?: string;
        due_date?: string;
        tags?: string[];
        watcher_ids?: string[];
      };
      const payload: CreateTaskPayload = { title: newTask.title, board_id: boardId, priority: newTask.priority };
      if (statusId) payload.status_id = statusId;
      if (newTask.assignee_id) payload.assignee_id = newTask.assignee_id;
      if (newTask.due_date) payload.due_date = newTask.due_date;
      if (newTask.tags.length > 0) payload.tags = newTask.tags;
      if (newTask.watcher_ids.length > 0) payload.watcher_ids = newTask.watcher_ids;

      const res = await apiClient.post('/tasks', payload);
      if (res.success) {
        toast({ title: 'Task creato' });
        setShowCreateTask(false);
        setNewTask({ title: '', board_id: '', status_id: '', priority: 'none', assignee_id: '', due_date: '', tags: [], watcher_ids: [] });
        await loadTasks();
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Eliminare questo task?')) return;
    try {
      const res = await apiClient.delete(`/tasks/${taskId}`);
      if (res.success) {
        toast({ title: 'Task eliminato' });
        setShowTaskDetail(false);
        setSelectedTask(null);
        await loadTasks();
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const openTaskDetail = async (task: TaskItem) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
    setDetailTab('info');
    setEditTitle(task.title);
    setEditDescription(task.description || '');
    setEditingTitle(false);
    try {
      const [detailRes, commentsRes, activitiesRes] = await Promise.all([
        apiClient.get(`/tasks/${task.id}`),
        apiClient.get<TaskComment[]>(`/tasks/${task.id}/comments`),
        apiClient.get<TaskActivity[]>(`/tasks/${task.id}/activities`),
      ]);
      if (detailRes.success && detailRes.data) {
        const detail = detailRes.data as TaskDetail;
        setTaskDetail(detail);
        setSubtasks(detail.subtasks || []);
        setEditDescription(detail.description || '');
      }
      if (commentsRes.success && Array.isArray(commentsRes.data)) setComments(commentsRes.data);
      if (activitiesRes.success && Array.isArray(activitiesRes.data)) setActivities(activitiesRes.data);

    } catch (e) { console.error('openTaskDetail error', e); }
  };

  const handleUpdateTask = async (taskId: string, updates: Record<string, unknown>) => {
    if (!taskId) return;
    setSavingTask(true);
    try {
      const res = await apiClient.put(`/tasks/${taskId}`, updates);
      if (res.success) {
        await loadTasks();
        if (showTaskDetail && selectedTask?.id === taskId) {
          const detailRes = await apiClient.get(`/tasks/${taskId}`);
          if (detailRes.success && detailRes.data) {
            const detail = detailRes.data as TaskDetail;
            setTaskDetail(detail);
            setSelectedTask(detail);
            setSubtasks(detail.subtasks || []);
          }
        }
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
    setSavingTask(false);
  };

  const handleMoveTask = async (taskId: string, statusId: string, position?: number) => {
    if (!taskId) return;
    try {
      await apiClient.put(`/tasks/${taskId}/move`, { status_id: statusId, position: position || 0 });
      await loadTasks();
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleAddComment = async () => {
    if (!selectedTask || !newComment.trim()) return;
    try {
      await apiClient.post(`/tasks/${selectedTask.id}/comments`, { content: newComment });
      setNewComment('');
      const res = await apiClient.get<TaskComment[]>(`/tasks/${selectedTask.id}/comments`);
      if (res.success && res.data) setComments(res.data);
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleAddSubtask = async () => {
    if (!selectedTask || !newSubtask.trim()) return;
    try {
      await apiClient.post(`/tasks/${selectedTask.id}/subtasks`, { title: newSubtask });
      setNewSubtask('');
      const res = await apiClient.get(`/tasks/${selectedTask.id}`);
      if (res.success && res.data) {
        const detail = res.data as TaskDetail;
        setSubtasks(detail.subtasks || []);
        setTaskDetail(detail);
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleToggleSubtask = async (subtaskId: string, completed: boolean) => {
    if (!selectedTask) return;
    try {
      await apiClient.put(`/tasks/subtasks/${subtaskId}`, { is_completed: completed });
      const res = await apiClient.get(`/tasks/${selectedTask.id}`);
      if (res.success && res.data) {
        const detail = res.data as TaskDetail;
        setSubtasks(detail.subtasks || []);
        setTaskDetail(detail);
      }
      await loadTasks();
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleStartTimer = async () => {
    if (!selectedTask) return;
    try {
      await apiClient.post(`/tasks/${selectedTask.id}/time/start`, {});
      await loadActiveTimer();
      toast({ title: 'Timer avviato' });
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleStopTimer = async () => {
    if (!selectedTask) return;
    try {
      await apiClient.post(`/tasks/${selectedTask.id}/time/stop`, {});
      setActiveTimer(null);
      setTimerSeconds(0);
      if (timerInterval.current) clearInterval(timerInterval.current);
      toast({ title: 'Timer fermato' });
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleVideoMeeting = async () => {
    if (!selectedTask) return;
    try {
      const res = await apiClient.post<{ id: string }>('/video/rooms', {
        title: `Video: ${selectedTask.title}`,
        taskId: selectedTask.id
      });
      if (res.success && res.data) {
        const slug = window.location.pathname.split('/')[1] || '';
        navigate(`/${slug}/video/${res.data.id}`);
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    if (task) setDraggedTask(task);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setDraggedTask(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const overId = over.id as string;

    const targetStatus = statuses.find(s => s.id === overId);
    if (targetStatus) {
      await handleMoveTask(taskId, targetStatus.id);
    }
  };

  const handleBoardSettingsOpen = async () => {
    if (!selectedBoardId) return;
    const board = boards.find(b => b.id === selectedBoardId);
    if (board) setEditBoard({ ...board });
    setBoardModalMode('edit');
    try {
      const [statusRes, wfStatsRes, wfRunsRes] = await Promise.all([
        apiClient.get<{ statuses?: BoardStatus[] }>(`/tasks/boards/${selectedBoardId}/statuses`),
        apiClient.getBoardWorkflowStats(selectedBoardId),
        apiClient.getBoardWorkflowRuns(selectedBoardId, { limit: 20 }),
      ]);
      if (statusRes.success && statusRes.data) setBoardStatuses(statusRes.data.statuses || []);
      // Task #3713 — metodi board-workflow tipizzati: niente cast.
      if (wfStatsRes.success) setBoardWorkflowStats(wfStatsRes.stats || []);
      if (wfRunsRes.success) setBoardWorkflowRuns(wfRunsRes.runs || []);
    } catch (e) {}
    setShowBoardSettings(true);
  };

  const loadBoardWorkflowData = async () => {
    if (!selectedBoardId) return;
    try {
      const [statsRes, runsRes] = await Promise.all([
        apiClient.getBoardWorkflowStats(selectedBoardId),
        apiClient.getBoardWorkflowRuns(selectedBoardId, { limit: 20 }),
      ]);
      // Task #3713 — metodi board-workflow tipizzati: niente cast.
      if (statsRes.success) setBoardWorkflowStats(statsRes.stats || []);
      if (runsRes.success) setBoardWorkflowRuns(runsRes.runs || []);
    } catch (e) {}
  };

  const handleOpenAddBoardWorkflow = async () => {
    try {
      const [workflowsRes, nodesRes] = await Promise.all([
        apiClient.get<{ workflows?: unknown[] }>('/workflows'),
        apiClient.get<{ nodes?: Record<string, unknown> }>('/workflows/available-nodes'),
      ]);
      const allWorkflows = ((workflowsRes as { workflows?: unknown[] }).workflows || workflowsRes.data?.workflows || []) as BoardAvailableWorkflow[];
      const nodesData = (nodesRes.data?.nodes || (nodesRes as { nodes?: Record<string, unknown> }).nodes || {}) as { triggers?: TriggerNode[] };
      const triggerNodes: TriggerNode[] = nodesData.triggers || [];
      const triggerIdsWithTask = triggerNodes
        .filter((n) => {
          const outProps = n.output_schema?.properties || {};
          return Object.keys(outProps).some(k => k === 'task_id' || outProps[k]?.context === 'task_id');
        })
        .map((n) => n.id);
      const compatibleTypes = new Set<string>();
      for (const tid of triggerIdsWithTask) {
        compatibleTypes.add(tid);
        if (tid.startsWith('trigger_')) compatibleTypes.add(tid.replace('trigger_', ''));
        else compatibleTypes.add(`trigger_${tid}`);
      }
      setBoardCompatibleTriggerTypes(Array.from(compatibleTypes));
      setBoardAvailableWorkflows(allWorkflows);
    } catch (e) {
      setBoardAvailableWorkflows([]);
    }
    setBoardWorkflowForm({ workflow_id: '' });
    setShowAddBoardWorkflow(true);
  };

  const handleAddBoardWorkflow = async () => {
    if (!selectedBoardId || !boardWorkflowForm.workflow_id) return;
    setSavingBoardWorkflow(true);
    try {
      const res = await apiClient.addBoardWorkflow(selectedBoardId, { workflow_id: boardWorkflowForm.workflow_id });
      if (res.success) {
        toast({ title: 'Workflow collegato alla board' });
        setShowAddBoardWorkflow(false);
        await loadBoardWorkflowData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore nel collegamento', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: 'Errore di rete', variant: 'destructive' });
    } finally {
      setSavingBoardWorkflow(false);
    }
  };

  const handleToggleBoardWorkflow = async (triggerId: string, currentActive: boolean) => {
    if (!selectedBoardId) return;
    try {
      await apiClient.updateBoardWorkflow(selectedBoardId, triggerId, { is_active: !currentActive });
      await loadBoardWorkflowData();
    } catch (e) {}
  };

  const handleRemoveBoardWorkflow = async (triggerId: string, name: string) => {
    if (!selectedBoardId) return;
    if (!confirm(`Rimuovere il workflow "${name}" da questa board?`)) return;
    try {
      await apiClient.removeBoardWorkflow(selectedBoardId, triggerId);
      toast({ title: 'Workflow rimosso dalla board' });
      await loadBoardWorkflowData();
    } catch (e) {}
  };


  const getEmbedCode = (formId: string) => {
    const baseUrl = getPublicBaseUrl();
    return `<div id="edg-ticket-form"></div>\n<script>\n(function(){var s='${baseUrl}/api/tasks/public/form-by-id/${formId}';fetch(s).then(function(r){return r.json()}).then(function(d){if(!d.success||!d.data)return;var f=d.data,c=document.getElementById('edg-ticket-form');if(!c)return;var h='<form id="edg-tf" style="max-width:500px;font-family:sans-serif"><h3>'+f.name+'</h3>';(f.fields||[]).forEach(function(fl){h+='<div style="margin:8px 0"><label>'+fl.label+(fl.required?' *':'')+'</label><br>';if(fl.type==='textarea')h+='<textarea name="'+fl.label+'" style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px" '+(fl.required?'required':'')+' rows="4"></textarea>';else h+='<input type="'+(fl.type||'text')+'" name="'+fl.label+'" style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px" '+(fl.required?'required':'')+'/>';h+='</div>'});h+='<button type="submit" style="background:#6366f1;color:#fff;border:none;padding:10px 20px;border-radius:6px;cursor:pointer;margin-top:8px">Invia</button></form>';c.innerHTML=h;document.getElementById('edg-tf').onsubmit=function(e){e.preventDefault();var fd={};new FormData(this).forEach(function(v,k){fd[k]=v});fetch(s+'/submit',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({data:fd})}).then(function(r){return r.json()}).then(function(r){if(r.success)c.innerHTML='<p style="color:green">'+(r.message||'Inviato!')+'</p>';else c.innerHTML='<p style="color:red">Errore nell\\'invio</p>'})}})})()\n</script>`;
  };

  const handleSaveBoardSettings = async (board: BoardDraft | null) => {
    if (!board) return;
    try {
      await apiClient.put(`/tasks/boards/${board.id}`, {
        name: board.name,
        description: board.description,
        icon: board.icon,
        color: board.color,
        is_template: board.is_template,
        workgroup_id: board.workgroup_id,
      });
      toast({ title: 'Board aggiornata' });
      setShowBoardSettings(false);
      await loadBoards();
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleAddStatus = async (name: string, color: string) => {
    if (!selectedBoardId || !name.trim()) return;
    try {
      await apiClient.post(`/tasks/boards/${selectedBoardId}/statuses`, { name, color });
      const res = await apiClient.get<{ statuses?: BoardStatus[] }>(`/tasks/boards/${selectedBoardId}/statuses`);
      if (res.success && res.data) {
        setBoardStatuses(res.data.statuses || []);
        setStatuses(res.data.statuses || []);
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const handleDeleteStatus = async (statusId: string) => {
    try {
      await apiClient.delete(`/tasks/statuses/${statusId}`);
      const res = await apiClient.get<{ statuses?: BoardStatus[] }>(`/tasks/boards/${selectedBoardId}/statuses`);
      if (res.success && res.data) {
        setBoardStatuses(res.data.statuses || []);
        setStatuses(res.data.statuses || []);
      }
    } catch (e) { toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' }); }
  };

  const filteredTasks = useMemo(() => {
    return tasks;
  }, [tasks]);

  const tasksByStatus = useMemo(() => {
    const map: Record<string, TaskItem[]> = {};
    statuses.forEach(s => { map[s.id] = []; });
    filteredTasks.forEach(t => {
      if (map[t.status_id]) map[t.status_id]!.push(t)!;
      else {
        const firstStatus = statuses[0];
        if (firstStatus && map[firstStatus.id]) map[firstStatus.id]!.push(t)!;
      }
    });
    Object.keys(map).forEach(k => { map[k]!.sort((a, b) => a.position - b.position)!; });
    return map;
  }, [filteredTasks, statuses]);

  const selectedBoard = boards.find(b => b.id === selectedBoardId);

  const priorityOptions = useMemo(() => (
    Object.entries(PRIORITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label, emoji: v.emoji }))
  ), []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const renderMobileCards = () => (
    <div className="space-y-2">
      {filteredTasks.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">Nessun task trovato</div>
      )}
      {statuses.map(status => {
        const statusTasks = filteredTasks.filter(t => t.status_id === status.id);
        if (statusTasks.length === 0) return null;
        return (
          <div key={status.id}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: status.color }} />
              <span className="text-xs font-semibold text-muted-foreground">{status.name}</span>
              <Badge variant="outline" className="text-[10px] ml-auto">{statusTasks.length}</Badge>
            </div>
            <div className="space-y-2 mb-4">
              {statusTasks.map(task => {
                const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.none;
                const isOverdue = task.due_date && !task.completed_at && isPast(new Date(task.due_date));
                return (
                  <div
                    key={task.id}
                    className="bg-card rounded-lg border border-border shadow-sm p-3 cursor-pointer active:bg-muted"
                    onClick={() => openTaskDetail(task)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] font-mono text-muted-foreground">{task.code}</span>
                          <Badge className={`${priority!.bg!} ${priority!.color!} text-[9px] px-1.5 py-0 border-0`}>{priority!.emoji!} {priority!.label!}</Badge>
                        </div>
                        <h4 className="text-sm font-semibold text-foreground leading-snug">{task.title}</h4>
                      </div>
                      {task.assignee_first_name && (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0">
                          {task.assignee_first_name[0]}{task.assignee_last_name?.[0] || ''}
                        </div>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-relaxed">{task.description}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {isOverdue && (
                        <Badge className="bg-red-500/10 dark:bg-red-500/20 text-red-700 dark:text-red-300 text-[9px] px-1.5 py-0.5">
                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Scaduto
                        </Badge>
                      )}
                      {task.due_date && !isOverdue && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                          <CalendarIcon className="w-2.5 h-2.5 mr-0.5" />{format(new Date(task.due_date), 'dd/MM/yy')}
                        </Badge>
                      )}
                      {task.appointment_date && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-purple-600 dark:text-purple-300">
                          <Clock className="w-2.5 h-2.5 mr-0.5" />{format(new Date(task.appointment_date), 'dd/MM HH:mm')}
                        </Badge>
                      )}
                      {(task.comments_count || 0) > 0 && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <MessageSquare className="w-3 h-3" />{task.comments_count}
                        </span>
                      )}
                    </div>
                    {task.tags && task.tags.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {task.tags.slice(0, 3).map(tag => (
                          <span key={tag.id} className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: tag.color + '25', color: tag.color }}>{tag.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderKanban = () => (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 280px)' }}>
        {statuses.map(status => {
          const count = (tasksByStatus[status.id] || []).length;
          return (
            <div key={status.id} className="flex-shrink-0 w-72">
              <div
                className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg"
                style={{
                  background: aurT.panel2,
                  border: `1px solid ${aurT.border}`,
                }}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: status.color, boxShadow: `0 0 0 3px ${status.color}33` }} />
                <span className="text-[12px] font-semibold uppercase tracking-[0.08em]" style={{ color: aurT.text }}>{status.name}</span>
                <span
                  className="ml-auto text-[10px] tabular-nums px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: aurT.panel, color: aurT.muted, border: `1px solid ${aurT.border}` }}
                >
                  {count}
                </span>
              </div>
              <DroppableColumn id={status.id}>
                <SortableContext items={(tasksByStatus[status.id] || []).map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {(tasksByStatus[status.id] || []).map(task => (
                      <SortableTaskCard key={task.id} task={task} onClick={() => openTaskDetail(task)} />
                    ))}
                    {count === 0 && (
                      <div
                        className="text-center text-[11px] py-6 rounded-lg"
                        style={{ color: aurT.faint, border: `1px dashed ${aurT.border}` }}
                      >
                        Trascina qui un task
                      </div>
                    )}
                  </div>
                </SortableContext>
              </DroppableColumn>
            </div>
          );
        })}
      </div>
      <DragOverlay>
        {draggedTask && (
          <div
            className="rounded-xl border p-3 w-72 opacity-95"
            style={{
              background: aurT.panel,
              borderColor: aurT.primary,
              boxShadow: `0 18px 40px -12px ${aurT.primary}77`,
              color: aurT.text,
            }}
          >
            <div className="text-[10px] font-mono mb-1" style={{ color: aurT.muted }}>{draggedTask.code}</div>
            <div className="text-sm font-semibold">{draggedTask.title}</div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );

  const renderDataTable = () => (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: aurT.panel, border: `1px solid ${aurT.border}` }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: aurT.panel2, borderBottom: `1px solid ${aurT.border}` }}>
              <th className="px-3 py-2.5 text-left w-8">
                <Checkbox checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0} onCheckedChange={(c) => setSelectedTasks(c ? filteredTasks.map(t => t.id) : [])} />
              </th>
              {['Codice','Titolo','Stato','Priorità','Assegnatario','Scadenza','Appuntamento','Tags','Azioni'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.08em]" style={{ color: aurT.muted }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-12 text-[13px]" style={{ color: aurT.faint }}>Nessun task trovato</td>
              </tr>
            )}
            {filteredTasks.map(task => {
              const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.none;
              const status = statuses.find(s => s.id === task.status_id);
              const isOverdue = task.due_date && !task.completed_at && isPast(new Date(task.due_date));
              return (
                <tr
                  key={task.id}
                  className="cursor-pointer transition-colors"
                  style={{ borderBottom: `1px solid ${aurT.border}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = aurT.panel2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => openTaskDetail(task)}
                >
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <Checkbox checked={selectedTasks.includes(task.id)} onCheckedChange={(c) => setSelectedTasks(prev => c ? [...prev, task.id] : prev.filter(id => id !== task.id))} />
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]" style={{ color: aurT.muted }}>{task.code}</td>
                  <td className="px-3 py-2 font-medium max-w-[220px] truncate" style={{ color: aurT.text }}>{task.title}</td>
                  <td className="px-3 py-2">
                    {status && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
                        style={{ background: `${status.color}20`, color: status.color, border: `1px solid ${status.color}55` }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
                        {status.name}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`${priority!.bg!} ${priority!.color!} text-xs border-0`}>{priority!.emoji!} {priority!.label!}</Badge>
                  </td>
                  <td className="px-3 py-2 text-[12px]" style={{ color: aurT.muted }}>
                    {task.assignee_first_name ? `${task.assignee_first_name} ${task.assignee_last_name || ''}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-[12px]" style={{ color: isOverdue ? '#ef4444' : aurT.muted, fontWeight: isOverdue ? 600 : 400 }}>
                    {task.due_date ? format(new Date(task.due_date), 'dd/MM/yyyy') : '—'}
                  </td>
                  <td className="px-3 py-2 text-[12px]" style={{ color: aurT.muted }}>
                    {task.appointment_date ? format(new Date(task.appointment_date), 'dd/MM HH:mm') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {(task.tags || []).slice(0, 2).map(tag => (
                        <span key={tag.id} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tag.color + '25', color: tag.color }}>{tag.name}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => openTaskDetail(task)}
                      className="w-8 h-8 grid place-items-center rounded-md transition-colors"
                      style={{ color: aurT.muted }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = aurT.panel; e.currentTarget.style.color = aurT.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = aurT.muted; }}
                      aria-label="Apri dettaglio"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedTasks.length > 0 && (
        <div
          className="flex items-center gap-2 p-3"
          style={{ background: `${aurT.primary}12`, borderTop: `1px solid ${aurT.border}` }}
        >
          <span className="text-[13px] font-semibold" style={{ color: aurT.primary }}>{selectedTasks.length} selezionati</span>
          <Select onValueChange={async (statusId) => {
            try {
              await apiClient.put('/tasks/bulk/move', { task_ids: selectedTasks, status_id: statusId });
              setSelectedTasks([]);
              await loadTasks();
              toast({ title: 'Task spostati' });
            } catch (e) { toast({ title: 'Errore', variant: 'destructive' }); }
          }}>
            <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Sposta a..." /></SelectTrigger>
            <SelectContent>
              {statuses.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  const renderEisenhower = () => {
    const quadrants = [
      { key: 'urgent_important', label: 'Urgente + Importante', subtitle: 'FARE SUBITO', accent: '#ef4444' },
      { key: 'not_urgent_important', label: 'Importante', subtitle: 'PIANIFICARE', accent: '#f59e0b' },
      { key: 'urgent_not_important', label: 'Urgente', subtitle: 'DELEGARE', accent: '#f97316' },
      { key: 'not_urgent_not_important', label: 'Né urgente né importante', subtitle: 'ELIMINARE', accent: aurT.muted },
    ];

    const getQuadrantTasks = (priority: string) => filteredTasks.filter(t => t.priority === priority);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quadrants.map(q => {
          const qTasks = getQuadrantTasks(q.key);
          return (
            <div
              key={q.key}
              className="rounded-xl p-4 min-h-[250px]"
              style={{
                background: aurT.panel,
                border: `1px solid ${aurT.border}`,
                boxShadow: `inset 4px 0 0 0 ${q.accent}`,
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-[13px]" style={{ color: q.accent }}>{q.label}</h3>
                  <p className="text-[10px] uppercase tracking-[0.12em] mt-0.5" style={{ color: aurT.faint }}>{q.subtitle}</p>
                </div>
                <span
                  className="text-[11px] tabular-nums px-2 py-0.5 rounded-md font-semibold"
                  style={{ background: `${q.accent}1A`, color: q.accent, border: `1px solid ${q.accent}40` }}
                >
                  {qTasks.length}
                </span>
              </div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {qTasks.map(task => (
                  <div
                    key={task.id}
                    className="rounded-lg p-2.5 cursor-pointer transition-all"
                    style={{ background: aurT.panel2, border: `1px solid ${aurT.border}` }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = q.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = aurT.border; }}
                    onClick={() => openTaskDetail(task)}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] font-mono" style={{ color: aurT.muted }}>{task.code}</span>
                    </div>
                    <p className="text-[13px] font-medium truncate" style={{ color: aurT.text }}>{task.title}</p>
                    {task.assignee_first_name && (
                      <p className="text-[11px] mt-1" style={{ color: aurT.muted }}>{task.assignee_first_name} {task.assignee_last_name || ''}</p>
                    )}
                  </div>
                ))}
                {qTasks.length === 0 && (
                  <p className="text-[12px] text-center py-6" style={{ color: aurT.faint }}>Nessun task</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  const renderKPIDashboard = () => {
    if (!boardStats) return <div className="text-center py-8 text-muted-foreground">Caricamento statistiche...</div>;

    const statusData = (boardStats.by_status || []).map(s => ({ name: s.name, value: s.count, color: s.color }));
    const priorityData = (boardStats.by_priority || []).map(p => ({
      name: (PRIORITY_CONFIG[p.priority] || PRIORITY_CONFIG.none)!.label,
      value: p.count,
    }));
    const assigneeData = (boardStats.by_assignee || []).map(a => ({ name: a.name || 'Non assegnato', value: a.count }));

    const KpiCard = ({ value, label, accent }: { value: React.ReactNode; label: string; accent: string }) => (
      <div
        className="rounded-xl p-4"
        style={{ background: aurT.panel, border: `1px solid ${aurT.border}`, boxShadow: `inset 3px 0 0 0 ${accent}` }}
      >
        <div className="text-[24px] font-semibold tabular-nums" style={{ color: accent }}>{value}</div>
        <div className="text-[11px] uppercase tracking-[0.08em] mt-1" style={{ color: aurT.muted }}>{label}</div>
      </div>
    );

    const ChartPanel = ({ title, children }: { title: string; children: React.ReactNode }) => (
      <div className="rounded-xl p-4" style={{ background: aurT.panel, border: `1px solid ${aurT.border}` }}>
        <div className="text-[12px] font-semibold uppercase tracking-[0.1em] mb-3" style={{ color: aurT.muted }}>{title}</div>
        {children}
      </div>
    );

    const tooltipStyle = { backgroundColor: aurT.panel, borderColor: aurT.border, color: aurT.text, borderRadius: 8 };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard value={boardStats.total} label="Task Totali" accent={aurT.primary} />
          <KpiCard value={boardStats.completed_this_week || 0} label="Completati (settimana)" accent={aurT.success} />
          <KpiCard value={boardStats.overdue_count || 0} label="In Ritardo" accent="#ef4444" />
          <KpiCard value={boardStats.avg_completion_hours ? `${Math.round(boardStats.avg_completion_hours)}h` : '—'} label="Tempo Medio" accent={aurT.ai} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartPanel title="Per Stato">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={tooltipStyle} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : <p className="text-center py-8" style={{ color: aurT.faint }}>Nessun dato</p>}
          </ChartPanel>
          <ChartPanel title="Per Assegnatario">
            {assigneeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={assigneeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={aurT.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: aurT.muted }} stroke={aurT.border} />
                  <YAxis allowDecimals={false} tick={{ fill: aurT.muted }} stroke={aurT.border} />
                  <RechartsTooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="value" fill={aurT.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-8" style={{ color: aurT.faint }}>Nessun dato</p>}
          </ChartPanel>
        </div>

        {boardStats.overdue_count > 0 && (
          <div className="rounded-xl p-4" style={{ background: aurT.panel, border: `1px solid ${aurT.border}`, boxShadow: 'inset 3px 0 0 0 #ef4444' }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4" style={{ color: '#ef4444' }} />
              <div className="text-[12px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#ef4444' }}>Task in Ritardo</div>
            </div>
            <div className="space-y-1.5">
              {filteredTasks.filter(t => t.due_date && !t.completed_at && isPast(new Date(t.due_date))).slice(0, 10).map(task => (
                <button
                  key={task.id}
                  type="button"
                  className="w-full flex items-center justify-between gap-2 p-2 rounded-lg cursor-pointer text-left transition-colors"
                  style={{ background: aurT.panel2, border: `1px solid ${aurT.border}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ef444466'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = aurT.border; }}
                  onClick={() => openTaskDetail(task)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[11px] shrink-0" style={{ color: aurT.muted }}>{task.code}</span>
                    <span className="text-[13px] font-medium truncate" style={{ color: aurT.text }}>{task.title}</span>
                  </div>
                  <span className="text-[11px] font-medium tabular-nums shrink-0" style={{ color: '#ef4444' }}>
                    {task.due_date ? format(new Date(task.due_date), 'dd/MM/yyyy') : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTaskDetail = () => {
    if (!selectedTask) return null;
    const priority = PRIORITY_CONFIG[selectedTask.priority] || PRIORITY_CONFIG.none;
    const status = statuses.find(s => s.id === selectedTask.status_id);
    const subtaskPct = subtasks.length > 0 ? Math.round(subtasks.filter(s => s.is_completed).length / subtasks.length * 100) : 0;

    const controlStyle: React.CSSProperties = { background: aurT.panel, borderColor: aurT.border, color: aurT.text };
    const labelStyle: React.CSSProperties = { color: aurT.muted };
    const surfaceStyle: React.CSSProperties = { background: aurT.panel2, borderColor: aurT.border };

    return (
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent
          hideClose
          className="max-w-4xl max-h-[90vh] overflow-hidden p-0 shadow-xl"
          style={{ background: aurT.panel, borderColor: aurT.border }}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{selectedTask.code} - {selectedTask.title}</DialogTitle>
          <div
            className="sticky top-0 z-10 px-5 py-3"
            style={{
              background: `linear-gradient(180deg, ${aurT.panel} 0%, ${aurT.panel2} 100%)`,
              borderBottom: `1px solid ${aurT.border}`,
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span
                  className="text-[11px] font-mono px-2 py-0.5 rounded-md shrink-0"
                  style={{ background: aurT.panel2, color: aurT.muted, border: `1px solid ${aurT.border}` }}
                >
                  {selectedTask.code}
                </span>
                {status && (
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: status.color, boxShadow: `0 0 0 3px ${status.color}33` }} />
                )}
                {editingTitle ? (
                  <div className="flex items-center gap-1 flex-1">
                    <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="text-lg font-semibold h-9" style={controlStyle} autoFocus onKeyDown={e => { if (e.key === 'Enter') { handleUpdateTask(selectedTask.id, { title: editTitle }); setEditingTitle(false); } }} />
                    <Button size="sm" variant="ghost" onClick={() => { handleUpdateTask(selectedTask.id, { title: editTitle }); setEditingTitle(false); }}><Save className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <h2
                    className="text-[18px] font-semibold truncate cursor-pointer transition-colors"
                    style={{ color: aurT.text, fontFamily: aurora.serif }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = aurT.primary; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = aurT.text; }}
                    onClick={() => { setEditTitle(selectedTask.title); setEditingTitle(true); }}
                  >
                    {selectedTask.title}
                  </h2>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="text-red-500/70 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10" onClick={() => handleDeleteTask(selectedTask.id)}><Trash2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" style={{ color: aurT.muted }} onClick={() => setShowTaskDetail(false)}><X className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Select value={selectedTask.status_id} onValueChange={val => handleMoveTask(selectedTask.id, val)}>
                <SelectTrigger className="w-40 h-8" style={controlStyle}>
                  <SelectValue>
                    {status && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: status.color }} />
                        <span className="text-xs">{status.name}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedTask.priority} onValueChange={val => handleUpdateTask(selectedTask.id, { priority: val })}>
                <SelectTrigger className="w-48 h-8" style={controlStyle}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}><span className={v.color}>{v.emoji} {v.label}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden" style={{ maxHeight: 'calc(90vh - 110px)' }}>
            <div className="flex-1 overflow-y-auto" style={{ borderRight: `1px solid ${aurT.border}` }}>
              <Tabs value={detailTab} onValueChange={setDetailTab} className="px-5 pb-5">
                <TabsList className="grid grid-cols-5 w-full mt-3 bg-muted/60">
                  <TabsTrigger value="info" className="text-xs">Info</TabsTrigger>
                  <TabsTrigger value="subtasks" className="text-xs">Subtask</TabsTrigger>
                  <TabsTrigger value="time" className="text-xs">Tempo</TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs">Commenti</TabsTrigger>
                  <TabsTrigger value="activity" className="text-xs">Attività</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-xs font-medium" style={labelStyle}>Descrizione</Label>
                    <Textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} className="mt-1.5 min-h-[80px]" style={controlStyle} placeholder="Aggiungi descrizione..." onBlur={() => { if (editDescription !== (taskDetail?.description || '')) handleUpdateTask(selectedTask.id, { description: editDescription }); }} />
                  </div>

                  <Separator style={{ background: aurT.border }} />

                  <div>
                    <Label className="text-xs font-medium mb-2 block" style={labelStyle}>Tags</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {(taskDetail?.tags || selectedTask.tags || []).map((tag: TaskTag) => (
                        <Badge key={tag.id} className="text-xs cursor-pointer hover:opacity-70 border" style={{ backgroundColor: tag.color + '25', color: tag.color, borderColor: tag.color + '50' }} onClick={async () => {
                          await apiClient.delete(`/tasks/${selectedTask.id}/tags/${tag.id}`);
                          const res = await apiClient.get(`/tasks/${selectedTask.id}`);
                          if (res.success && res.data) { const d = res.data as TaskDetail; setTaskDetail(d); setSelectedTask(d); }
                          await loadTasks();
                        }}>
                          {tag.name} <X className="w-2.5 h-2.5 ml-1" />
                        </Badge>
                      ))}
                      <Select onValueChange={async (tagId) => {
                        await apiClient.post(`/tasks/${selectedTask.id}/tags/${tagId}`, {});
                        const res = await apiClient.get(`/tasks/${selectedTask.id}`);
                        if (res.success && res.data) { const d = res.data as TaskDetail; setTaskDetail(d); setSelectedTask(d); }
                        await loadTasks();
                      }}>
                        <SelectTrigger className="w-28 h-7 text-xs border-dashed" style={controlStyle}><SelectValue placeholder="+ Tag" /></SelectTrigger>
                        <SelectContent>
                          {tags.filter(t => !(taskDetail?.tags || selectedTask.tags || []).find((tt: TaskTag) => tt.id === t.id)).map(t => (
                            <SelectItem key={t.id} value={t.id}><span style={{ color: t.color }}>{t.name}</span></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator style={{ background: aurT.border }} />

                  <div>
                    <Label className="text-xs font-medium mb-1 block" style={labelStyle}>Dipendenze</Label>
                    {(taskDetail?.blocked_by || []).length > 0 && (
                      <div className="space-y-1 mb-2">
                        <span className="text-[10px] font-medium text-red-500 uppercase tracking-wider">Bloccato da:</span>
                        {(taskDetail?.blocked_by || []).map((dep) => (
                          <div key={dep.id} className="flex items-center gap-2 text-xs bg-red-500/10 dark:bg-red-500/20 rounded-md p-2 border border-red-500/30">
                            <span className="font-mono" style={labelStyle}>{dep.code}</span>
                            <span style={{ color: aurT.text }}>{dep.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(taskDetail?.blocking || []).length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-medium text-orange-500 uppercase tracking-wider">Blocca:</span>
                        {(taskDetail?.blocking || []).map((dep) => (
                          <div key={dep.id} className="flex items-center gap-2 text-xs bg-orange-500/10 dark:bg-orange-500/20 rounded-md p-2 border border-orange-500/30">
                            <span className="font-mono" style={labelStyle}>{dep.code}</span>
                            <span style={{ color: aurT.text }}>{dep.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(taskDetail?.blocked_by || []).length === 0 && (taskDetail?.blocking || []).length === 0 && (
                      <p className="text-xs" style={labelStyle}>Nessuna dipendenza</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="subtasks" className="mt-4">
                  <div className="space-y-2">
                    {subtasks.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <Progress value={subtaskPct} className="flex-1 h-2" />
                        <span className="text-xs font-medium" style={labelStyle}>{subtasks.filter(s => s.is_completed).length}/{subtasks.length}</span>
                      </div>
                    )}
                    {subtasks.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 group p-1.5 rounded-md hover:bg-muted/50 border border-transparent hover:border-border">
                        <Checkbox checked={sub.is_completed} onCheckedChange={(c) => handleToggleSubtask(sub.id, c as boolean)} />
                        <span className={`text-sm flex-1 ${sub.is_completed ? 'line-through' : ''}`} style={{ color: sub.is_completed ? aurT.muted : aurT.text }}>{sub.title}</span>
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100" onClick={async () => {
                          await apiClient.delete(`/tasks/subtasks/${sub.id}`);
                          const res = await apiClient.get(`/tasks/${selectedTask.id}`);
                          if (res.success && res.data) { const d = res.data as TaskDetail; setSubtasks(d.subtasks || []); setTaskDetail(d); }
                        }}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 mt-2">
                      <Input value={newSubtask} onChange={e => setNewSubtask(e.target.value)} placeholder="Nuova subtask..." className="h-8 text-sm" style={controlStyle} onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask(); }} />
                      <Button size="sm" onClick={handleAddSubtask} disabled={!newSubtask.trim()}><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="time" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg border" style={surfaceStyle}>
                      <Timer className="w-5 h-5 text-blue-500" />
                      <div className="flex-1">
                        {activeTimer && activeTimer.is_running ? (
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-mono font-bold text-primary">{formatDuration(timerSeconds)}</span>
                            <Button size="sm" variant="destructive" onClick={handleStopTimer}><Square className="w-3.5 h-3.5 mr-1" /> Stop</Button>
                          </div>
                        ) : (
                          <Button size="sm" onClick={handleStartTimer}><Play className="w-3.5 h-3.5 mr-1" /> Avvia Timer</Button>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={handleVideoMeeting} className="text-indigo-600 dark:text-indigo-300 border-indigo-500/30 hover:bg-indigo-500/10">
                        <Video className="w-3.5 h-3.5 mr-1" /> Video Meeting
                      </Button>
                      {((taskDetail?.time_stats?.total_seconds || taskDetail?.time_total_seconds || 0)) > 0 && (
                        <div className="text-right">
                          <div className="text-xs" style={labelStyle}>Totale</div>
                          <div className="text-sm font-semibold" style={{ color: aurT.text }}>{formatDuration(taskDetail?.time_stats?.total_seconds || taskDetail?.time_total_seconds || 0)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="comments" className="mt-4">
                  <div className="space-y-3">
                    {comments.map(comment => (
                      <div key={comment.id} className="flex gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0">
                          {comment.user_first_name?.[0] || '?'}{comment.user_last_name?.[0] || ''}
                        </div>
                        <div className="flex-1 rounded-lg p-3 border" style={surfaceStyle}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold" style={{ color: aurT.text }}>{comment.user_first_name} {comment.user_last_name}</span>
                            <span className="text-[10px]" style={labelStyle}>{format(new Date(comment.created_at), 'dd/MM HH:mm')}</span>
                          </div>
                          <p className="text-sm" style={{ color: aurT.text }}>{comment.content}</p>
                        </div>
                      </div>
                    ))}
                    {comments.length === 0 && <p className="text-xs text-center py-4" style={labelStyle}>Nessun commento</p>}
                    <Separator style={{ background: aurT.border }} />
                    <div className="flex gap-2">
                      <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Scrivi un commento..." className="min-h-[60px] text-sm" style={controlStyle} />
                      <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()} className="self-end"><MessageSquare className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-2">
                    {activities.map(act => (
                      <div key={act.id} className="flex items-start gap-2 text-xs p-2 rounded-md hover:bg-muted/50">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <span style={{ color: aurT.text }}>
                            {act.user_first_name && <strong style={{ color: aurT.text }}>{act.user_first_name} {act.user_last_name} </strong>}
                            {act.action === 'created' && 'ha creato il task'}
                            {act.action === 'status_changed' && `ha cambiato stato da "${act.old_value}" a "${act.new_value}"`}
                            {act.action === 'assigned' && `ha assegnato a ${act.new_value || 'nessuno'}`}
                            {act.action === 'priority_changed' && `ha cambiato priorità`}
                            {act.action === 'updated' && 'ha aggiornato il task'}
                            {act.action === 'changed_estimated_hours' && `ha modificato le ore stimate${act.new_value ? ` a ${act.new_value}h` : ''}`}
                            {act.action === 'changed_due_date' && `ha modificato la scadenza${act.new_value ? ` a ${act.new_value}` : ''}`}
                            {act.action === 'changed_start_date' && `ha modificato la data inizio${act.new_value ? ` a ${act.new_value}` : ''}`}
                            {act.action === 'changed_description' && 'ha modificato la descrizione'}
                            {act.action === 'changed_title' && 'ha modificato il titolo'}
                            {act.action === 'changed_board' && `ha spostato il task${act.new_value ? ` nella board "${act.new_value}"` : ''}`}
                            {act.action === 'changed_appointment' && `ha modificato l'appuntamento${act.new_value ? ` a ${act.new_value}` : ''}`}
                            {act.action === 'watcher_added' && `ha aggiunto un osservatore`}
                            {act.action === 'watcher_removed' && `ha rimosso un osservatore`}
                            {act.action === 'tag_added' && `ha aggiunto il tag "${act.new_value || ''}"`}
                            {act.action === 'tag_removed' && `ha rimosso il tag "${act.old_value || ''}"`}
                            {act.action === 'subtask_added' && 'ha aggiunto una subtask'}
                            {act.action === 'subtask_completed' && 'ha completato una subtask'}
                            {act.action === 'comment_added' && 'ha aggiunto un commento'}
                            {!['created', 'status_changed', 'assigned', 'priority_changed', 'updated', 'changed_estimated_hours', 'changed_due_date', 'changed_start_date', 'changed_description', 'changed_title', 'changed_board', 'changed_appointment', 'watcher_added', 'watcher_removed', 'tag_added', 'tag_removed', 'subtask_added', 'subtask_completed', 'comment_added'].includes(act.action) && act.action.replace(/_/g, ' ')}
                          </span>
                          <span className="ml-2" style={labelStyle}>{format(new Date(act.created_at), 'dd/MM HH:mm')}</span>
                        </div>
                      </div>
                    ))}
                    {activities.length === 0 && <p className="text-xs text-center py-4" style={labelStyle}>Nessuna attività</p>}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="w-64 shrink-0 overflow-y-auto p-4 space-y-5" style={{ background: aurT.panel2 }}>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Assegnatario</Label>
                <Select value={selectedTask.assignee_id || 'unassigned'} onValueChange={val => handleUpdateTask(selectedTask.id, { assignee_id: val === 'unassigned' ? null : val })}>
                  <SelectTrigger className="h-8 text-sm" style={controlStyle}><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Nessuno</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={labelStyle}><Eye className="w-3 h-3" /> Osservatori</Label>
                <div className="space-y-1">
                  {(taskDetail?.watchers || []).map((w) => (
                    <div key={w.id || w.user_id} className="flex items-center gap-1.5 text-xs group/watcher" style={labelStyle}>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-[8px] font-medium">
                        {w.first_name?.[0] || '?'}{w.last_name?.[0] || ''}
                      </div>
                      <span className="truncate flex-1">{w.first_name} {w.last_name}</span>
                      <button className="opacity-0 group-hover/watcher:opacity-100 hover:text-red-600 dark:hover:text-red-400 transition-opacity" onClick={async () => {
                        await apiClient.delete(`/tasks/${selectedTask.id}/watchers/${w.user_id || w.id}`);
                        const res = await apiClient.get(`/tasks/${selectedTask.id}`);
                        if (res.success && res.data) { const d = res.data as TaskDetail; setTaskDetail(d); setSelectedTask(d); }
                      }}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <Select onValueChange={async (userId) => {
                    await apiClient.post(`/tasks/${selectedTask.id}/watchers`, { user_id: userId });
                    const res = await apiClient.get(`/tasks/${selectedTask.id}`);
                    if (res.success && res.data) { const d = res.data as TaskDetail; setTaskDetail(d); setSelectedTask(d); }
                  }}>
                    <SelectTrigger className="h-7 text-xs border-dashed mt-1" style={controlStyle}><SelectValue placeholder="+ Aggiungi osservatore" /></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => !(taskDetail?.watchers || []).find((w) => (w.user_id || w.id) === u.id)).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Board</Label>
                <Select value={selectedTask.board_id} onValueChange={val => handleUpdateTask(selectedTask.id, { board_id: val })}>
                  <SelectTrigger className="h-8 text-sm" style={controlStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Data Inizio</Label>
                <Input type="date" className="h-8 text-sm" style={controlStyle} value={selectedTask.start_date ? format(new Date(selectedTask.start_date), 'yyyy-MM-dd') : ''} onChange={e => handleUpdateTask(selectedTask.id, { start_date: e.target.value || null })} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Scadenza</Label>
                <Input type="date" className="h-8 text-sm" style={controlStyle} value={selectedTask.due_date ? format(new Date(selectedTask.due_date), 'yyyy-MM-dd') : ''} onChange={e => handleUpdateTask(selectedTask.id, { due_date: e.target.value || null })} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Appuntamento</Label>
                <Input type="datetime-local" className="h-8 text-sm" style={controlStyle} value={selectedTask.appointment_date ? format(new Date(selectedTask.appointment_date), "yyyy-MM-dd'T'HH:mm") : ''} onChange={e => handleUpdateTask(selectedTask.id, { appointment_date: e.target.value || null })} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Durata (min)</Label>
                <Input type="number" className="h-8 text-sm" style={controlStyle} value={selectedTask.appointment_duration || ''} onChange={e => handleUpdateTask(selectedTask.id, { appointment_duration: e.target.value ? parseInt(e.target.value) : null })} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider" style={labelStyle}>Ore Stimate</Label>
                <Input type="number" step="0.5" className="h-8 text-sm w-full" style={controlStyle} value={selectedTask.estimated_hours || ''} onChange={e => handleUpdateTask(selectedTask.id, { estimated_hours: e.target.value ? parseFloat(e.target.value) : null })} />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const handleEditBoardFromSidebar = async (board: Board) => {
    setSelectedBoardId(board.id);
    setEditBoard({ ...board });
    setBoardModalMode('edit');
    try {
      const statusRes = await apiClient.get<{ statuses?: BoardStatus[] }>(`/tasks/boards/${board.id}/statuses`);
      if (statusRes.success && statusRes.data) setBoardStatuses(statusRes.data.statuses || []);
    } catch (err) {}
    setShowBoardSettings(true);
  };

  const handleOpenCalendar = () => {
    const slug = window.location.pathname.split('/')[1] || '';
    navigate(`/${slug}/calendar?sources=tasks`);
  };

  return (
    <div
      className="flex h-[calc(100vh-64px)] relative overflow-hidden"
      style={{ background: aurT.bg }}
    >
      {/* Aurora ambient background blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-60"
        style={{
          background:
            'radial-gradient(800px 400px at 5% -10%, rgba(99,102,241,0.10), transparent 70%), radial-gradient(700px 380px at 100% 110%, rgba(168,85,247,0.10), transparent 70%)',
        }}
      />

      <BoardsAuroraSidebar
        boards={boards}
        selectedBoardId={selectedBoardId}
        pinned={sidebarPinned}
        onPinChange={setSidebarPinned}
        onSelectBoard={setSelectedBoardId}
        onCreateBoard={handleOpenCreateBoard}
        onEditBoard={(b) => handleEditBoardFromSidebar(b as Board)}
        onDeleteBoard={handleDeleteBoard}
      />

      <BoardsAuroraDrawer
        open={mobileSidebarOpen}
        onOpenChange={setMobileSidebarOpen}
        boards={boards}
        selectedBoardId={selectedBoardId}
        onSelectBoard={(id) => { setSelectedBoardId(id); setMobileSidebarOpen(false); }}
        onCreateBoard={() => { handleOpenCreateBoard(); setMobileSidebarOpen(false); }}
        onEditBoard={(b) => handleEditBoardFromSidebar(b as Board)}
        onDeleteBoard={handleDeleteBoard}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0 relative z-10">
        <TasksAuroraHeader
          board={selectedBoard || null}
          taskCount={tasks.length}
          isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
          activeView={activeView}
          onChangeView={handleChangeView}
          searchQuery={searchQuery}
          onChangeSearch={setSearchQuery}
          filterPriority={filterPriority || 'all'}
          onChangePriority={(v) => setFilterPriority(v === 'all' ? '' : v)}
          filterAssignee={filterAssignee || 'all'}
          onChangeAssignee={(v) => setFilterAssignee(v === 'all' ? '' : v)}
          priorities={priorityOptions}
          users={users}
          activeTimerLabel={activeTimer?.is_running ? formatDuration(timerSeconds) : null}
          onCreateTask={() => setShowCreateTask(true)}
          onOpenBoardSettings={handleBoardSettingsOpen}
          onOpenTaskSettings={() => setShowTaskSettings(true)}
          onOpenCalendar={handleOpenCalendar}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
          onOpenMobileFilters={() => setMobileFiltersOpen(true)}
          isMobile={isMobile}
        />

        <div className="flex-1 overflow-auto p-2 md:p-4">
          <div className="hidden md:block">
            {activeView === 'kanban' && renderKanban()}
            {activeView === 'table' && renderDataTable()}
            {activeView === 'eisenhower' && renderEisenhower()}
            {activeView === 'kpi' && renderKPIDashboard()}
          </div>
          <div className="md:hidden">
            {(activeView === 'kanban' || activeView === 'table') && renderMobileCards()}
            {activeView === 'eisenhower' && renderEisenhower()}
            {activeView === 'kpi' && renderKPIDashboard()}
          </div>
        </div>
      </div>

      <MobileFiltersSheet
        open={mobileFiltersOpen}
        onOpenChange={setMobileFiltersOpen}
        filterPriority={filterPriority || 'all'}
        onChangePriority={(v) => setFilterPriority(v === 'all' ? '' : v)}
        filterAssignee={filterAssignee || 'all'}
        onChangeAssignee={(v) => setFilterAssignee(v === 'all' ? '' : v)}
        priorities={priorityOptions}
        users={users}
        onClearAll={() => { setFilterPriority(''); setFilterAssignee(''); setSearchQuery(''); }}
      />

      <MobileNewTaskFab onClick={() => setShowCreateTask(true)} />

      {renderTaskDetail()}

      <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden"
          style={{ background: aurT.panel, borderColor: aurT.border }}
          aria-describedby={undefined}
        >
          <DialogHeader
            className="px-5 py-4"
            style={{
              background: `linear-gradient(180deg, ${aurT.panel} 0%, ${aurT.panel2} 100%)`,
              borderBottom: `1px solid ${aurT.border}`,
            }}
          >
            <DialogTitle className="flex items-center gap-2 text-[18px]" style={{ fontFamily: aurora.serif, color: aurT.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: aurT.primary, boxShadow: `0 0 0 3px ${aurT.primary}33` }} />
              Nuovo Task
            </DialogTitle>
          </DialogHeader>
          <div className="px-5 pt-4 pb-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Board *</Label>
              {boards.length === 0 ? (
                <div
                  className="mt-1 rounded-md border border-dashed px-3 py-2.5 text-xs leading-relaxed"
                  style={{ borderColor: aurT.border, color: aurT.muted, background: aurT.panel2 }}
                >
                  Nessuna board disponibile.{' '}
                  <button
                    type="button"
                    className="font-semibold underline underline-offset-2"
                    style={{ color: aurT.primary }}
                    onClick={() => { setShowCreateTask(false); handleOpenCreateBoard(); }}
                  >
                    Crea una board
                  </button>{' '}
                  per poter aggiungere un task.
                </div>
              ) : (
                <Select value={newTask.board_id || selectedBoardId} onValueChange={val => setNewTask(prev => ({ ...prev, board_id: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Seleziona una board" /></SelectTrigger>
                  <SelectContent>
                    {boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input value={newTask.title} onChange={e => setNewTask(prev => ({ ...prev, title: e.target.value }))} placeholder="Titolo del task..." className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Stato</Label>
                <Select value={newTask.status_id} onValueChange={val => setNewTask(prev => ({ ...prev, status_id: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priorità</Label>
                <Select value={newTask.priority} onValueChange={val => setNewTask(prev => ({ ...prev, priority: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.emoji} {v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Assegnatario</Label>
                <Select value={newTask.assignee_id} onValueChange={val => setNewTask(prev => ({ ...prev, assignee_id: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Scadenza</Label>
                <Input type="date" value={newTask.due_date} onChange={e => setNewTask(prev => ({ ...prev, due_date: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Osservatori</Label>
              <div className="mt-1 flex flex-wrap gap-1.5 p-2 border rounded-md min-h-[36px] bg-background">
                {newTask.watcher_ids.map(wId => {
                  const wu = users.find(u => u.id === wId);
                  return wu ? (
                    <span key={wId} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                      {wu.first_name} {wu.last_name}
                      <button type="button" onClick={() => setNewTask(prev => ({ ...prev, watcher_ids: prev.watcher_ids.filter(id => id !== wId) }))} className="hover:text-blue-900 dark:hover:text-blue-200">×</button>
                    </span>
                  ) : null;
                })}
                <Select value="" onValueChange={val => { if (val && !newTask.watcher_ids.includes(val)) setNewTask(prev => ({ ...prev, watcher_ids: [...prev.watcher_ids, val] })); }}>
                  <SelectTrigger className="h-6 w-auto min-w-[100px] border-0 shadow-none text-xs p-0 pl-1"><SelectValue placeholder="+ Aggiungi..." /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => !newTask.watcher_ids.includes(u.id)).map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          </div>
          <DialogFooter className="px-5 py-4 sm:px-5 sm:py-4 sm:gap-3 sm:space-x-0" style={{ background: aurT.panel2, borderTop: `1px solid ${aurT.border}` }}>
            <Button variant="outline" onClick={() => setShowCreateTask(false)}>Annulla</Button>
            <Button onClick={handleCreateTask} disabled={!newTask.title.trim() || (!newTask.board_id && !selectedBoardId)}>Crea Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskSettingsModal
        open={showTaskSettings}
        onOpenChange={setShowTaskSettings}
      />

      <BoardSettingsModal
        open={showBoardSettings}
        onOpenChange={setShowBoardSettings}
        mode={boardModalMode}
        board={editBoard}
        boardStatuses={boardStatuses}
        boardWorkflowStats={boardWorkflowStats}
        boardWorkflowRuns={boardWorkflowRuns}
        users={users}
        existingBoardNames={boards.map(b => b.name.toLowerCase().trim())}
        onSave={handleSaveBoardSettings}
        onCreate={handleCreateBoard}
        onDeleteBoard={handleDeleteBoard}
        onAddStatus={(name, color) => handleAddStatus(name, color)}
        onDeleteStatus={handleDeleteStatus}
        onOpenAddWorkflow={handleOpenAddBoardWorkflow}
        onToggleWorkflow={handleToggleBoardWorkflow}
        onRemoveWorkflow={handleRemoveBoardWorkflow}
        getEmbedCode={getEmbedCode}
      />

      <Dialog open={showAddBoardWorkflow} onOpenChange={setShowAddBoardWorkflow}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600 dark:text-purple-300" />
              Collega Workflow alla Board
            </DialogTitle>
            <DialogDescription>
              Il trigger e la configurazione sono definiti nel workflow stesso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {boardAvailableWorkflows.length > 0 && boardAvailableWorkflows.some((wf) => 
              boardCompatibleTriggerTypes.length > 0 && !boardCompatibleTriggerTypes.includes(wf.trigger_type ?? '')
            ) && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/30 rounded-lg">
                <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  I workflow disabilitati hanno un trigger non compatibile con le Task Board. Per poterli collegare, il trigger deve avere <code className="bg-amber-500/20 dark:bg-amber-500/30 px-1 rounded">task_id</code> nell'output (es. task creata, task aggiornata).
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Workflow <span className="text-red-500">*</span></Label>
              <Select value={boardWorkflowForm.workflow_id} onValueChange={v => setBoardWorkflowForm({ workflow_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un workflow..." />
                </SelectTrigger>
                <SelectContent>
                  {boardAvailableWorkflows.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                      Nessun workflow disponibile.<br />
                      <button type="button" className="text-purple-600 dark:text-purple-300 underline text-xs mt-1" onClick={() => navigate('../workflow-builder')}>Vai al Workflow Builder</button>
                    </div>
                  ) : (
                    <>
                      {boardAvailableWorkflows
                        .sort((a, b) => {
                          const aCompat = boardCompatibleTriggerTypes.length === 0 || boardCompatibleTriggerTypes.includes(a.trigger_type ?? '');
                          const bCompat = boardCompatibleTriggerTypes.length === 0 || boardCompatibleTriggerTypes.includes(b.trigger_type ?? '');
                          if (aCompat && !bCompat) return -1;
                          if (!aCompat && bCompat) return 1;
                          return (a.name || '').localeCompare(b.name || '');
                        })
                        .map((wf) => {
                          const isCompatible = boardCompatibleTriggerTypes.length === 0 || boardCompatibleTriggerTypes.includes(wf.trigger_type ?? '');
                          const alreadyLinked = boardWorkflowStats.some((s) => s.workflow_id === wf.id);
                          const isDisabled = !isCompatible || alreadyLinked;
                          return (
                            <SelectItem key={wf.id} value={wf.id} disabled={isDisabled}>
                              <div className="flex items-center gap-2">
                                <span className={isDisabled ? 'text-muted-foreground' : ''}>{wf.name}</span>
                                {wf.trigger_type && (
                                  <Badge variant="outline" className={`text-[10px] ${isCompatible ? 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30' : 'bg-muted text-muted-foreground border-border'}`}>
                                    {wf.trigger_type?.replace(/^trigger_/, '').replace(/_/g, ' ')}
                                  </Badge>
                                )}
                                {!wf.is_active && <Badge className="text-xs bg-muted text-muted-foreground">inattivo</Badge>}
                                {alreadyLinked && <Badge className="text-xs bg-primary/15 text-primary">già collegato</Badge>}
                              </div>
                            </SelectItem>
                          );
                        })}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            {boardWorkflowForm.workflow_id && (() => {
              const sel = boardAvailableWorkflows.find((w) => w.id === boardWorkflowForm.workflow_id);
              if (!sel) return null;
              const nodes = Array.isArray(sel.nodes) ? sel.nodes.filter((n) => n.type !== 'trigger' && n.type !== 'start') : [];
              return (
                <div className="p-3 bg-purple-500/10 dark:bg-purple-500/15 rounded-lg border border-purple-500/30 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30">{sel.trigger_type || 'trigger'}</Badge>
                    {sel.trigger_config && Object.keys(sel.trigger_config).length > 0 && (
                      <span className="text-[10px] text-muted-foreground">{JSON.stringify(sel.trigger_config)}</span>
                    )}
                  </div>
                  {nodes.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-[10px] text-muted-foreground mr-1">Azioni:</span>
                      {nodes.slice(0, 5).map((n: BoardWorkflowNode, i: number) => (
                        <Badge key={i} variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">{n.data?.label || n.type}</Badge>
                      ))}
                      {nodes.length > 5 && <span className="text-[10px] text-muted-foreground">+{nodes.length - 5}</span>}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBoardWorkflow(false)}>Annulla</Button>
            <Button onClick={handleAddBoardWorkflow} disabled={savingBoardWorkflow || !boardWorkflowForm.workflow_id}>
              {savingBoardWorkflow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GitBranch className="w-4 h-4 mr-2" />}
              Collega
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
